var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
export var FocusableItemDirectionEnum;
(function (FocusableItemDirectionEnum) {
    FocusableItemDirectionEnum["Horizontal"] = "horizontal";
    FocusableItemDirectionEnum["Vertical"] = "vertical";
})(FocusableItemDirectionEnum || (FocusableItemDirectionEnum = {}));
var KEYS = {
    LEFT: [37],
    UP: [38],
    RIGHT: [39],
    DOWN: [40],
    ENTER: [13],
    BACK: [8, 461, 27],
    EXIT: [403, 67],
    TOP: [90, 406],
};
// Pre-computed set for preventDefault check
var PREVENTABLE_KEYS = new Set(__spreadArrays(KEYS.UP, KEYS.DOWN, KEYS.LEFT, KEYS.RIGHT, KEYS.ENTER));
// O(1) keycode → action lookup
var KEY_ACTION = new Map();
KEYS.LEFT.forEach(function (k) { return KEY_ACTION.set(k, "LEFT"); });
KEYS.RIGHT.forEach(function (k) { return KEY_ACTION.set(k, "RIGHT"); });
KEYS.UP.forEach(function (k) { return KEY_ACTION.set(k, "UP"); });
KEYS.DOWN.forEach(function (k) { return KEY_ACTION.set(k, "DOWN"); });
KEYS.ENTER.forEach(function (k) { return KEY_ACTION.set(k, "ENTER"); });
KEYS.BACK.forEach(function (k) { return KEY_ACTION.set(k, "BACK"); });
var FocusManager = /** @class */ (function () {
    function FocusManager() {
        var _this = this;
        this.groups = new Map();
        this.groupOrder = [];
        this.groupIndexMap = new Map();
        this.groupOrderDirty = false;
        this.currentGroup = "main-navbar";
        this.currentIndex = 0;
        this.isEnabled = true;
        this.layerStack = [];
        this.activeLayerGroupName = null;
        this.activeLayerIsolated = false;
        this.pendingUpdate = false;
        this.lastFocusedNode = null;
        this.lastMoveDirection = null;
        this.direction = FocusableItemDirectionEnum.Horizontal;
        this.forceHorizontalRecenter = false;
        this.pendingVerticalFocus = null;
        this.offsets = new Map();
        this.viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
        this.resizeHandler = null;
        // Cached parent rects — invalidated on resize
        this.parentRects = new Map();
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.tick = this.tick.bind(this);
        if (typeof window !== "undefined") {
            window.addEventListener("keydown", this.handleKeyDown);
            this.resizeHandler = function () {
                _this.viewportHeight = window.innerHeight;
                _this.parentRects.clear();
            };
            window.addEventListener("resize", this.resizeHandler);
        }
    }
    FocusManager.prototype.targetMap = function (groupName) {
        if (this.activeLayerIsolated &&
            this.layerStack.length > 0 &&
            groupName !== this.activeLayerGroupName) {
            return this.layerStack[this.layerStack.length - 1].groups;
        }
        return this.groups;
    };
    FocusManager.prototype.removeGroup = function (groupName) {
        var _a;
        this.targetMap(groupName).delete(groupName);
        this.groupOrderDirty = true;
        if (((_a = this.pendingVerticalFocus) === null || _a === void 0 ? void 0 : _a.groupName) === groupName) {
            this.pendingVerticalFocus = null;
        }
    };
    FocusManager.prototype.addGroupOrder = function (groupName, position) {
        this.registerGroup({ name: groupName, position: position });
    };
    FocusManager.prototype.registerGroup = function (_a) {
        var groupName = _a.name, position = _a.position, itemHeight = _a.itemHeight, horizontalContainer = _a.horizontalContainer, verticalContainer = _a.verticalContainer, resetIndexOnVerticalMove = _a.resetIndexOnVerticalMove;
        var map = this.targetMap(groupName);
        if (!map.has(groupName)) {
            map.set(groupName, {
                name: groupName,
                items: [],
                itemsById: new Map(),
                savedIndex: 0,
                position: position || 0,
                itemHeight: itemHeight || 0,
                horizontalContainer: horizontalContainer,
                verticalContainer: verticalContainer,
                resetIndexOnVerticalMove: resetIndexOnVerticalMove,
            });
        }
        else {
            var group = map.get(groupName);
            if (position !== undefined)
                group.position = position;
            if (itemHeight !== undefined)
                group.itemHeight = itemHeight;
            if (horizontalContainer !== undefined)
                group.horizontalContainer = horizontalContainer;
            if (verticalContainer !== undefined)
                group.verticalContainer = verticalContainer;
            if (resetIndexOnVerticalMove !== undefined)
                group.resetIndexOnVerticalMove = resetIndexOnVerticalMove;
        }
        this.groupOrderDirty = true;
    };
    FocusManager.prototype.ensureGroupOrder = function () {
        var _this = this;
        if (!this.groupOrderDirty)
            return;
        this.groupOrder.length = 0;
        this.groups.forEach(function (_, name) { return _this.groupOrder.push(name); });
        this.groupOrder.sort(function (a, b) { return _this.groups.get(a).position - _this.groups.get(b).position; });
        this.groupIndexMap.clear();
        for (var i = 0; i < this.groupOrder.length; i++) {
            this.groupIndexMap.set(this.groupOrder[i], i);
        }
        this.groupOrderDirty = false;
    };
    FocusManager.prototype.updateGroupOrder = function () {
        this.groupOrderDirty = true;
    };
    FocusManager.prototype.clearGroupsAndResetOrder = function () {
        this.groups.clear();
        this.groupOrder.length = 0;
        this.groupIndexMap.clear();
        this.groupOrderDirty = false;
        this.offsets.clear();
        this.parentRects.clear();
        this.pendingVerticalFocus = null;
    };
    // Drop the per-container scroll/layout caches without touching groups or the
    // current focus. These maps are keyed by scroll-container elements and are
    // never otherwise pruned, so a left screen's detached container stays pinned
    // here — keeping its whole poster subtree (and textures) alive. Safe to call
    // on navigation: the incoming screen rebuilds its entries on the next tick.
    FocusManager.prototype.clearScrollCaches = function () {
        this.offsets.clear();
        this.parentRects.clear();
    };
    FocusManager.prototype.getGroupTotalHeight = function (group) {
        var _a;
        if (!group.itemHeight)
            return 0;
        if (((_a = group.items[0]) === null || _a === void 0 ? void 0 : _a.direction) === FocusableItemDirectionEnum.Vertical) {
            return group.itemHeight * group.items.length;
        }
        return group.itemHeight;
    };
    FocusManager.prototype.binaryInsert = function (items, item) {
        var lo = 0;
        var hi = items.length;
        while (lo < hi) {
            var mid = (lo + hi) >>> 1;
            if (items[mid].index < item.index)
                lo = mid + 1;
            else
                hi = mid;
        }
        items.splice(lo, 0, item);
    };
    FocusManager.prototype.register = function (groupName, index, id, node, direction, onClick, onBack) {
        var _a;
        var map = this.targetMap(groupName);
        if (!map.has(groupName)) {
            map.set(groupName, {
                name: groupName,
                items: [],
                itemsById: new Map(),
                savedIndex: 0,
                position: 0,
                itemHeight: 0,
            });
            this.groupOrderDirty = true;
        }
        var group = map.get(groupName);
        var existing = group.itemsById.get(id);
        var wasEmpty = group.items.length === 0;
        if (existing) {
            existing.node = node;
            existing.index = index;
            existing.onClick = onClick;
            existing.onBack = onBack;
            existing.direction = direction;
        }
        else {
            var newItem = {
                id: id,
                group: groupName,
                index: index,
                node: node,
                onClick: onClick,
                onBack: onBack,
                direction: direction || FocusableItemDirectionEnum.Horizontal,
            };
            group.itemsById.set(id, newItem);
            this.binaryInsert(group.items, newItem);
        }
        if (this.currentGroup === groupName && this.currentIndex === index) {
            this.requestTick();
        }
        if (wasEmpty &&
            this.pendingVerticalFocus &&
            this.pendingVerticalFocus.groupName === groupName) {
            var pending = this.pendingVerticalFocus;
            this.pendingVerticalFocus = null;
            var targetIdx = pending.resetToStart
                ? 0
                : Math.min(group.items.length - 1, pending.preferredIndex);
            this.currentGroup = groupName;
            this.currentIndex = targetIdx;
            group.savedIndex = targetIdx;
            this.forceHorizontalRecenter = true;
            this.requestTick();
            if (typeof window !== "undefined") {
                this.ensureGroupOrder();
                window.dispatchEvent(new CustomEvent("focus-group-change", {
                    detail: {
                        group: groupName,
                        index: (_a = this.groupIndexMap.get(groupName)) !== null && _a !== void 0 ? _a : -1,
                        total: this.groupOrder.length,
                    },
                }));
            }
        }
    };
    FocusManager.prototype.unregister = function (groupName, id) {
        var _a;
        var map = this.targetMap(groupName);
        var group = map.get(groupName);
        if (group) {
            var item = group.itemsById.get(id);
            if (item) {
                group.itemsById.delete(id);
                var idx = group.items.indexOf(item);
                if (idx !== -1)
                    group.items.splice(idx, 1);
            }
            if (group.items.length === 0) {
                map.delete(groupName);
                this.groupOrderDirty = true;
                if (((_a = this.pendingVerticalFocus) === null || _a === void 0 ? void 0 : _a.groupName) === groupName) {
                    this.pendingVerticalFocus = null;
                }
            }
        }
    };
    FocusManager.prototype.handleKeyDown = function (event) {
        if (!this.isEnabled)
            return;
        var code = event.keyCode;
        var action = KEY_ACTION.get(code);
        if (!action)
            return;
        if (PREVENTABLE_KEYS.has(code)) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (action === "LEFT" || action === "RIGHT")
            this.lastMoveDirection = "HORIZONTAL";
        else if (action === "UP" || action === "DOWN")
            this.lastMoveDirection = "VERTICAL";
        if (!this.direction) {
            this.direction = FocusableItemDirectionEnum.Horizontal;
        }
        if (action === "ENTER") {
            this.handleEnter();
            return;
        }
        if (action === "BACK") {
            this.handleBack();
            return;
        }
        if (this.direction === FocusableItemDirectionEnum.Horizontal) {
            this.move(action);
        }
        else {
            if (action === "UP")
                this.move("LEFT");
            else if (action === "DOWN")
                this.move("RIGHT");
        }
    };
    FocusManager.prototype.move = function (direction) {
        var _a;
        this.ensureGroupOrder();
        var group = this.groups.get(this.currentGroup);
        if (!group || group.items.length === 0)
            return;
        var targetIndex = this.currentIndex;
        var targetGroup = this.currentGroup;
        if (direction === "LEFT") {
            this.pendingVerticalFocus = null;
            targetIndex = Math.max(0, this.currentIndex - 1);
        }
        else if (direction === "RIGHT") {
            this.pendingVerticalFocus = null;
            targetIndex = Math.min(group.items.length - 1, this.currentIndex + 1);
        }
        else if (direction === "UP" || direction === "DOWN") {
            var currentGroupOrderIndex = this.groupIndexMap.get(this.currentGroup);
            if (currentGroupOrderIndex === undefined)
                return;
            var nextGroupIndex = direction === "DOWN"
                ? currentGroupOrderIndex + 1
                : currentGroupOrderIndex - 1;
            if (nextGroupIndex >= 0 && nextGroupIndex < this.groupOrder.length) {
                var nextGroupName = this.groupOrder[nextGroupIndex];
                var nextGroup = this.groups.get(nextGroupName);
                if (nextGroup) {
                    if (nextGroup.items.length > 0) {
                        targetGroup = nextGroupName;
                        targetIndex = nextGroup.resetIndexOnVerticalMove
                            ? 0
                            : Math.min(nextGroup.items.length - 1, this.currentIndex);
                    }
                    else {
                        // Row is registered in order but hasn't committed items yet.
                        // Remember the intended move so register() can finish it once
                        // the row actually populates, instead of dead-ending here.
                        this.pendingVerticalFocus = {
                            groupName: nextGroupName,
                            preferredIndex: this.currentIndex,
                            resetToStart: !!nextGroup.resetIndexOnVerticalMove,
                        };
                        if (direction === "DOWN" && typeof window !== "undefined") {
                            window.dispatchEvent(new Event("focus-end-reached"));
                        }
                    }
                }
            }
            else if (direction === "DOWN") {
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new Event("focus-end-reached"));
                }
            }
        }
        if (targetGroup !== this.currentGroup ||
            targetIndex !== this.currentIndex) {
            if (targetGroup !== this.currentGroup) {
                group.savedIndex = this.currentIndex;
                this.pendingVerticalFocus = null;
                this.forceHorizontalRecenter = true;
            }
            this.currentGroup = targetGroup;
            this.currentIndex = targetIndex;
            var newGroup = this.groups.get(targetGroup);
            if (newGroup)
                newGroup.savedIndex = targetIndex;
            this.requestTick();
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent("focus-group-change", {
                    detail: {
                        group: targetGroup,
                        index: (_a = this.groupIndexMap.get(targetGroup)) !== null && _a !== void 0 ? _a : -1,
                        total: this.groupOrder.length,
                    },
                }));
            }
        }
    };
    FocusManager.prototype.handleEnter = function () {
        var currentItem = this.getCurrentItem();
        if (currentItem === null || currentItem === void 0 ? void 0 : currentItem.onClick)
            currentItem.onClick();
    };
    FocusManager.prototype.handleBack = function () {
        var currentItem = this.getCurrentItem();
        if (currentItem === null || currentItem === void 0 ? void 0 : currentItem.onBack) {
            currentItem.onBack();
            return;
        }
        if (this.layerStack.length > 0) {
            this.popLayer();
        }
    };
    FocusManager.prototype.getCurrentItem = function () {
        var group = this.groups.get(this.currentGroup);
        if (!group)
            return undefined;
        return group.items[this.currentIndex] || group.items[0];
    };
    FocusManager.prototype.requestTick = function () {
        if (!this.pendingUpdate) {
            this.pendingUpdate = true;
            requestAnimationFrame(this.tick);
        }
    };
    FocusManager.prototype.getParentRect = function (container) {
        var parent = container.parentElement;
        if (!parent)
            return container.getBoundingClientRect();
        var cached = this.parentRects.get(parent);
        if (!cached) {
            cached = parent.getBoundingClientRect();
            this.parentRects.set(parent, cached);
        }
        return cached;
    };
    FocusManager.prototype.getOffset = function (container) {
        var offset = this.offsets.get(container);
        if (!offset) {
            offset = { x: 0, y: 0, writtenX: NaN, writtenY: NaN };
            this.offsets.set(container, offset);
        }
        return offset;
    };
    FocusManager.prototype.tick = function () {
        var group = this.groups.get(this.currentGroup);
        var item = (group === null || group === void 0 ? void 0 : group.items[this.currentIndex]) || (group === null || group === void 0 ? void 0 : group.items[0]);
        if (!(item === null || item === void 0 ? void 0 : item.node)) {
            this.pendingUpdate = false;
            this.forceHorizontalRecenter = false;
            return;
        }
        this.direction = item.direction;
        if (this.lastFocusedNode !== item.node) {
            if (this.lastFocusedNode) {
                this.lastFocusedNode.classList.remove("is-focused");
            }
            item.node.classList.add("is-focused");
            item.node.focus({ preventScroll: true });
        }
        var forceHorizontal = this.forceHorizontalRecenter;
        this.forceHorizontalRecenter = false;
        var hasHorizontal = (this.lastMoveDirection !== "VERTICAL" || forceHorizontal) &&
            !!group.horizontalContainer;
        var hasVertical = (this.lastMoveDirection === "VERTICAL" ||
            this.lastMoveDirection === null) &&
            !!group.verticalContainer;
        if (hasHorizontal || hasVertical) {
            var rect = item.node.getBoundingClientRect();
            // --- Horizontal Scroll ---
            if (hasHorizontal) {
                var container = group.horizontalContainer;
                var offset = this.getOffset(container);
                var parentRect = this.getParentRect(container);
                var targetLeft = parentRect.width / 2 - rect.width / 2;
                var diffX = rect.left - parentRect.left - targetLeft;
                offset.x -= diffX;
                if (offset.x > 0)
                    offset.x = 0;
                if (offset.x !== offset.writtenX) {
                    container.style.transform = "translateX(" + offset.x + "px)";
                    offset.writtenX = offset.x;
                }
            }
            // --- Vertical Scroll ---
            if (hasVertical) {
                var container = group.verticalContainer;
                var offset = this.getOffset(container);
                var parentRect = this.getParentRect(container);
                var viewportHeight = parentRect.height || this.viewportHeight;
                var parentTop = parentRect.top;
                var targetTop = viewportHeight / 2 - rect.height / 2;
                var diffY = rect.top - parentTop - targetTop;
                // Total content height from pre-set itemHeights — no DOM measurement
                this.ensureGroupOrder();
                var contentHeight = 0;
                for (var i = 0; i < this.groupOrder.length; i++) {
                    var g = this.groups.get(this.groupOrder[i]);
                    if (g)
                        contentHeight += this.getGroupTotalHeight(g);
                }
                if (contentHeight <= 0)
                    contentHeight = container.scrollHeight;
                offset.y -= diffY;
                var minY = Math.min(0, viewportHeight - contentHeight);
                if (offset.y < minY)
                    offset.y = minY;
                if (offset.y > 0)
                    offset.y = 0;
                if (offset.y !== offset.writtenY) {
                    container.style.transform = "translateY(" + offset.y + "px)";
                    offset.writtenY = offset.y;
                }
            }
        }
        this.lastFocusedNode = item.node;
        this.pendingUpdate = false;
    };
    FocusManager.prototype.destroy = function () {
        if (typeof window !== "undefined") {
            window.removeEventListener("keydown", this.handleKeyDown);
            if (this.resizeHandler) {
                window.removeEventListener("resize", this.resizeHandler);
                this.resizeHandler = null;
            }
        }
        this.groups.clear();
        this.offsets.clear();
        this.parentRects.clear();
        this.groupIndexMap.clear();
        this.pendingVerticalFocus = null;
    };
    FocusManager.prototype.jumpToTop = function () {
        this.lastMoveDirection = null;
        this.pendingVerticalFocus = null;
        this.offsets.forEach(function (offset, container) {
            container.style.transform = "translateY(0px)";
        });
        this.offsets.clear();
        this.manualFocus("main-navbar", 0);
    };
    FocusManager.prototype.getCurrentGroup = function () {
        return this.currentGroup;
    };
    FocusManager.prototype.manualFocus = function (groupName, itemIndex, force) {
        if (force === void 0) { force = false; }
        this.pendingVerticalFocus = null;
        if (force) {
            this.currentGroup = groupName;
            this.currentIndex = itemIndex;
            return;
        }
        var group = this.groups.get(groupName);
        if (group) {
            this.currentGroup = groupName;
            var itemIdx = group.items.findIndex(function (i) { return i.index === itemIndex; });
            this.currentIndex = itemIdx !== -1 ? itemIdx : 0;
            this.forceHorizontalRecenter = true;
            this.requestTick();
        }
    };
    FocusManager.prototype.isKey = function (key, name) {
        var _a, _b;
        return (_b = (_a = KEYS[name]) === null || _a === void 0 ? void 0 : _a.includes(key)) !== null && _b !== void 0 ? _b : false;
    };
    FocusManager.prototype.pushLayer = function (_a) {
        var groupName = _a.groupName, index = _a.index, isolate = _a.isolate;
        var currentGroup = this.groups.get(this.currentGroup);
        if (currentGroup) {
            currentGroup.savedIndex = this.currentIndex;
        }
        this.layerStack.push({
            groups: this.groups,
            currentGroup: this.currentGroup,
            prevLayerGroupName: this.activeLayerGroupName,
            prevLayerIsolated: this.activeLayerIsolated,
        });
        this.groups = new Map();
        this.activeLayerGroupName = groupName;
        this.activeLayerIsolated = !!isolate;
        this.groupOrderDirty = true;
        this.pendingVerticalFocus = null;
        this.ensureGroupOrder();
        this.currentGroup = groupName;
        this.currentIndex = index;
        this.forceHorizontalRecenter = true;
        this.requestTick();
    };
    FocusManager.prototype.popLayer = function () {
        if (this.layerStack.length > 0) {
            var prev = this.layerStack.pop();
            if (prev) {
                this.groups = prev.groups;
                this.currentGroup = prev.currentGroup;
                this.activeLayerGroupName = prev.prevLayerGroupName;
                this.activeLayerIsolated = prev.prevLayerIsolated;
                this.pendingVerticalFocus = null;
                var oldGroup = prev.groups.get(prev.currentGroup);
                if (oldGroup) {
                    this.currentIndex = oldGroup.savedIndex || 0;
                    var oldItem = oldGroup.items[this.currentIndex] || oldGroup.items[0];
                    this.direction = oldItem
                        ? oldItem.direction
                        : FocusableItemDirectionEnum.Horizontal;
                }
                this.groupOrderDirty = true;
                this.forceHorizontalRecenter = true;
                this.ensureGroupOrder();
                this.requestTick();
            }
        }
    };
    return FocusManager;
}());
export { FocusManager };
export var focusManager = new FocusManager();



//////////////////
// WEBPACK FOOTER
// ./src/utils/Focus.ts
// module id = 3
// module chunks = 1